/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// The field's gust ages (wave-ages.ts), read back from the GPU a frame or more late. The lift
// pass writes each live wave's age at each cell. Right after it, the ages get copied aside and
// fenced, and once the fence has passed, a frame reads the copy before it draws, so the read
// never waits on drawing. Only one read is in flight at a time, so a GPU that runs behind just
// reads less often, but it still reads.
//
// (A pixel-pack buffer needed no wait at all, but Chrome warned on every read, 70 times a wave,
// even with the textbook pattern.)
//
// Two records take turns: a read fills the one the rest of the page isn't holding, and neither
// is ever reallocated. What the copy holds waits in `pending` until it's read.
//
//   lift pass ──► fence(): blit the ages aside, fence, note the waves and the grid
//   next draws ─► collect(): fence passed? read the copy into the free record, swap

import { NO_AGES, type WaveAges } from "../waves/wave-ages.js";

// One record of the ages, which the readback fills in place (a `WaveAges`, which the rest of
// the page reads).
interface AgesRecord {
  ids: number[];
  cols: number;
  rows: number;
  cellW: number;
  cellH: number;
  originX: number;
  originY: number;
  data: Uint8Array;
}

/** Where the ages' grid sits on the page, in viewport CSS pixels. */
export interface AgesPlace {
  readonly cellW: number;
  readonly cellH: number;
  readonly originX: number;
  readonly originY: number;
}

const newRecord = (): AgesRecord => ({ ...NO_AGES, ids: [], data: new Uint8Array(0) });

export class AgesReadback {
  private front = newRecord();
  private back = newRecord();
  private readonly pending = newRecord();
  private sync: WebGLSync | null = null;
  /** The ages the rest of the page reads: the last read, or `NO_AGES`. */
  ages: WaveAges = NO_AGES;

  /** `lift` is the lift pass's framebuffer, with its ages on `COLOR_ATTACHMENT1`. `copy` is the
      framebuffer for the ages' copy, the same size as the grid. */
  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly lift: WebGLFramebuffer | null,
    private readonly copy: WebGLFramebuffer | null,
  ) {}

  /** Before a draw: once its fence has passed, the copy's ages become the current ages. */
  collect(): void {
    const { gl, sync } = this;
    if (!sync || gl.getSyncParameter(sync, gl.SYNC_STATUS) !== gl.SIGNALED) return;
    gl.deleteSync(sync);
    this.sync = null;
    const read = this.back;
    const { pending } = this;
    read.ids.length = 0;
    for (let i = 0; i < pending.ids.length; i++) read.ids.push(pending.ids[i]!);
    read.cols = pending.cols;
    read.rows = pending.rows;
    read.cellW = pending.cellW;
    read.cellH = pending.cellH;
    read.originX = pending.originX;
    read.originY = pending.originY;
    const size = read.cols * read.rows * 4;
    if (read.data.length !== size) read.data = new Uint8Array(size);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.copy);
    gl.readPixels(0, 0, read.cols, read.rows, gl.RGBA, gl.UNSIGNED_BYTE, read.data);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    this.back = this.front;
    this.front = read;
    this.ages = read;
  }

  /** After the lift pass: copies its ages aside and fences them, unless a read is already in
      flight, and notes what they hold: the waves' ids (one channel each) and the grid. */
  fence(
    waves: readonly { readonly id: number }[],
    cols: number,
    rows: number,
    place: AgesPlace,
  ): void {
    if (this.sync) return;
    const { gl } = this;
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.lift);
    gl.readBuffer(gl.COLOR_ATTACHMENT1);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.copy);
    gl.blitFramebuffer(0, 0, cols, rows, 0, 0, cols, rows, gl.COLOR_BUFFER_BIT, gl.NEAREST);
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    this.sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    const { pending } = this;
    pending.ids.length = 0;
    for (let i = 0; i < waves.length; i++) pending.ids.push(waves[i]!.id);
    pending.cols = cols;
    pending.rows = rows;
    pending.cellW = place.cellW;
    pending.cellH = place.cellH;
    pending.originX = place.originX;
    pending.originY = place.originY;
  }

  /** Stops a read in flight that nobody needs anymore (the copy was resized). */
  drop(): void {
    if (this.sync) this.gl.deleteSync(this.sync);
    this.sync = null;
  }

  /** No live waves: stops any read in flight, and there are no ages. */
  clear(): void {
    this.drop();
    this.ages = NO_AGES;
  }

  /** A lost context: no read can finish, and its sync went with it. */
  lose(): void {
    this.sync = null;
  }
}
